/**
 * <cart-promo-code> — applies and removes discount codes from the cart
 * footer (snippets/custom-cart-promo-code.liquid, both surfaces).
 *
 * /cart/update.js takes the WHOLE code set in its `discount` parameter, not a
 * delta — applying sends the applied tags plus the new code, removing sends
 * the tags minus one, and '' clears everything. The applied set is read back
 * out of the server-rendered tags, never held in JS state.
 *
 * Those tags live in the Savings row (custom-cart-totals.liquid), NOT inside
 * this element: a code's effect is already inside the savings figure, so it
 * belongs beside that label. This element therefore works against the footer
 * it shares with that snippet — .cart-drawer__footer in the drawer,
 * .js-contents on /cart — which scopes it to one surface even though the
 * drawer is rendered on /cart too.
 *
 * Shopify answers 200 for an invalid code AND for a valid-but-not-applicable
 * one (deliberately indistinguishable, to blunt code guessing), so success is
 * decided by reading the returned cart. On failure the previous set is
 * re-sent: the rejected code is still parked in the session otherwise, and
 * could start applying by itself once the cart changes.
 *
 * Success applies the response's sections via the shared cart section utils
 * and publishes cartUpdate with source 'cart-items' (Dawn's "already
 * rendered" signal) — which is what makes <cart-rewards> re-reconcile gifts
 * against the discounted total. Failure renders nothing: the cart did not
 * change, and a section swap would wipe the error message, which lives in
 * this element's own markup.
 */
if (!customElements.get('cart-promo-code')) {
  customElements.define(
    'cart-promo-code',
    class CartPromoCode extends HTMLElement {
      constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('.cart-promo__form');
        this.input = this.querySelector('.cart-promo__input');
        this.errorElement = this.querySelector('.cart-promo__error');
        // The footer shared with custom-cart-totals — the tags and their
        // remove buttons are in there, outside this element.
        this.scope = this.closest('.cart-drawer__footer, .js-contents') ?? this;
        this.form?.addEventListener('submit', this.onSubmit);
        this.scope.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
        this.scope?.removeEventListener('click', this.onClick);
      }

      /** The applied set, straight off the server-rendered tags. */
      appliedCodes() {
        return Array.from(this.scope.querySelectorAll('[data-applied-code]')).map((node) => node.dataset.appliedCode);
      }

      onSubmit(event) {
        event.preventDefault();
        const code = this.input?.value.trim();
        if (!code || this.busy) return;
        const applied = this.appliedCodes();
        // Already on the cart — nothing to send, just clear the field.
        if (applied.some((existing) => existing.toUpperCase() === code.toUpperCase())) {
          this.input.value = '';
          return;
        }
        this.update([...applied, code], code);
      }

      onClick(event) {
        const button = event.target.closest('[data-remove-code]');
        if (!button || this.busy) return;
        const code = button.dataset.removeCode;
        this.update(
          this.appliedCodes().filter((existing) => existing !== code),
          null,
        );
      }

      /**
       * @param {string[]} codes - the complete set to leave on the cart
       * @param {string|null} expected - the code just entered, whose
       *   application must be confirmed in the response; null when removing
       */
      async update(codes, expected) {
        this.setBusy(true);
        this.showError('');
        const sectionsToRender = cartSectionsToRender();
        try {
          const state = await this.post(codes, sectionsToRender);
          if (!state) {
            this.showError(this.dataset.textError);
            return;
          }
          if (expected && !CartPromoCode.isApplied(state, expected)) {
            this.showError(this.dataset.textInvalid);
            // Evict the rejected code from the session — Shopify keeps it
            // there, inert, and it would apply on its own once the cart
            // changes enough to make it valid.
            await this.post(codes.slice(0, -1), []);
            return;
          }
          if (state.sections) renderCartSections(state.sections, sectionsToRender);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
          }
          this.announce(expected ? this.dataset.textApplied : this.dataset.textRemoved);
        } catch (error) {
          console.warn('[cart-promo-code] error:', error);
          this.showError(this.dataset.textError);
        } finally {
          // Only reached on the failure paths in practice: a successful
          // update disconnects this element in the section swap above.
          if (this.isConnected) this.setBusy(false);
        }
      }

      /** @returns {Promise<object|null>} the updated cart, or null on a bad response */
      async post(codes, sectionsToRender) {
        const body = { discount: codes.join(',') };
        if (sectionsToRender.length) {
          body.sections = sectionsToRender.map((entry) => entry.section);
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify(body) });
        if (!response.ok) {
          console.warn('[cart-promo-code] update failed:', response.status);
          return null;
        }
        return response.json();
      }

      /**
       * Did `code` actually take? `discount_codes` carries the verdict
       * directly; the discount applications are the fallback for shapes that
       * omit it, matching on title (which for a code discount is the code).
       */
      static isApplied(cart, code) {
        const wanted = code.trim().toUpperCase();
        const matches = (value) => (value ?? '').toUpperCase() === wanted;
        const listed = (cart.discount_codes ?? []).find((entry) => matches(entry.code));
        if (listed) return listed.applicable !== false;
        // The fallback's entries are allocations in the Ajax shape — the title
        // sits on the nested application — but plain applications in others.
        return (
          (cart.cart_level_discount_applications ?? []).some(
            (entry) => matches(entry.title) || matches(entry.discount_application?.title),
          ) || (cart.items ?? []).some((item) => (item.discounts ?? []).some((entry) => matches(entry.title)))
        );
      }

      setBusy(busy) {
        this.busy = busy;
        this.classList.toggle('cart-promo--busy', busy);
        // Both this element's own controls and the remove buttons on the tags.
        this.querySelectorAll('button, input').forEach((node) => {
          node.disabled = busy;
        });
        this.scope.querySelectorAll('[data-remove-code]').forEach((node) => {
          node.disabled = busy;
        });
      }

      showError(text) {
        if (!this.errorElement) return;
        this.errorElement.textContent = text ?? '';
        this.classList.toggle('cart-promo--invalid', Boolean(text));
      }

      /** Announce on Dawn's cart status live region, same as <cart-line-toggle>. */
      announce(text) {
        if (!text) return;
        const region =
          document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
        if (!region) return;
        region.setAttribute('aria-hidden', 'false');
        region.textContent = text;
        setTimeout(() => region.setAttribute('aria-hidden', 'true'), 1000);
      }
    },
  );
}
